import React, { useRef, useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const useStyles = makeStyles({
  container: { 
    padding: '12px', 
    backgroundColor: '#ffffff', 
    fontFamily: 'Pretendard, sans-serif',
    width: '100%',
    boxSizing: 'border-box'
  },
  tableWrapper: {
    width: '100%',
    maxWidth: '100%', // 기존 1600px 제한을 풀어 브라우저 크기에 따라 늘어나도록 수정
    margin: '0 auto',
    backgroundColor: '#cbd5e0',
    border: '1px solid #cbd5e0',
    borderRadius: '2px',
    overflowX: 'auto', 
    boxSizing: 'border-box',
  },
  table: { 
    width: '100%', 
    borderCollapse: 'separate', 
    borderSpacing: '1px', 
    tableLayout: 'fixed', 
    minWidth: '1470px', // 최소 상태 유지
  },
  cell: {
    padding: '8px 12px', 
    backgroundColor: '#ffffff',
    fontSize: '0.875rem', 
    height: '40px', 
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    fontVariantNumeric: 'tabular-nums',
    display: 'table-cell',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerCell: { 
    fontWeight: '600', 
    textAlign: 'center', 
    fontSize: '0.875rem', 
  },
  
  bgBlue: { backgroundColor: '#e3f2fd !important' },
  bgOrange: { backgroundColor: '#fff3e0 !important' },
  highlightOrange: { backgroundColor: '#ffd8a8  !important'},
  bgGreen: { backgroundColor: '#e8f5e9 !important' },

  bgFirstCol: { backgroundColor: '#f5f5f5 !important', fontWeight: '700', textAlign: 'center' },
  bgSumRow: { 
    backgroundColor: '#edf2f7 !important', 
    color: '#000000 !important', 
    fontWeight: '600 !important', 
    fontSize: '0.875rem !important' 
  },

  headerControlWrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '100%' },
  arrowButton: {
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', 
    width: '24px', height: '24px', borderRadius: '50%', 
    transition: 'all 0.1s ease', color: '#4a5568', backgroundColor: 'rgba(0, 0, 0, 0.05)',
    '&:hover': { backgroundColor: '#3182ce', color: '#ffffff' },
  },
  arrowPlaceholder: { width: '24px' },

  scrollTd: { padding: '0 !important', backgroundColor: '#cbd5e0 !important' },
  scrollBoxHideBar: { 
    display: 'flex', 
    overflowX: 'hidden', 
    width: '100%', 
    height: '100%', 
    gap: '1px' 
  },
  scrollItem: {
    flex: 1, 
    minWidth: '125px',
    fontSize: '0.875rem !important', 
    height: '40px', 
    display: 'flex', 
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 12px', 
    boxSizing: 'border-box', 
    backgroundColor: '#ffffff',
  },
  
  monthHeaderFont: {
    fontSize: '0.875rem !important',
    fontWeight: '600 !important',
    textAlign: 'center !important',
  },

  txtRight: { textAlign: 'right' },
  txtCenter: { textAlign: 'center' },
  
  colDept: { width: '12.8%' },      
  colBudget: { width: '12.5%' },    
  colScrollArea: { width: '48%' },  
  colSummary: { width: '9%' },    
  colRate: { width: '6%' },       
  colBalance: { width: '12.7%' },   

  '@media (max-width: 1400px)': {
    cell: { padding: '8px 6px', fontSize: '0.825rem' },
    headerCell: { fontSize: '0.85rem' }
  }
});




const BudgetToActualDirectTable = ({deptList, searchYear, startMonth, endMonth}) => {
    const classes = useStyles();
  const startNum = parseInt(startMonth, 10) || 1;
  const endNum = parseInt(endMonth, 10) || 12;
  
  const months = Array.from(
    { length: Math.max(0, endNum - startNum + 1) }, 
    (_, i) => startNum + i
  );
  
  const scrollRefs = useRef([]);
  const timerRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const addToRefs = (el) => { 
    if (el && !scrollRefs.current.includes(el)) {
      scrollRefs.current.push(el); 
    }
  };

  const updateArrowVisibility = () => {
    const master = scrollRefs.current[0]; 
    if (master) {
      const { scrollLeft, scrollWidth, clientWidth } = master;
      setShowLeft(scrollLeft > 2);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  const stopScrolling = () => { 
    if (timerRef.current) { 
      clearInterval(timerRef.current); 
      timerRef.current = null; 
    } 
    updateArrowVisibility(); 
  };
  
  const startScrolling = (direction) => {
    stopScrolling();
    const speed = 15; 
    const move = () => { 
      scrollRefs.current.forEach((ref) => { 
        if (ref) ref.scrollLeft += (direction === 'next' ? speed : -speed); 
      }); 
      updateArrowVisibility(); 
    };
    move(); 
    timerRef.current = setInterval(move, 16);
  };

  const handleScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    scrollRefs.current.forEach((ref) => { 
      if (ref && ref !== e.target) ref.scrollLeft = scrollLeft; 
    });
    updateArrowVisibility();
  };

  const activeMonthRange = useMemo(() => {
    const start = parseInt(startMonth, 10);
    const end = parseInt(endMonth, 10);
    return Array.from({ length: end - start + 1 }, (_, i) => String(start + i).padStart(2, '0'));
  }, [startMonth, endMonth]);

  const rows = useMemo(() => {
    const data = deptList || [];
    return data.map(deptRow => {
      const budget = deptRow.strategyBudgetAmt || 0;
      
      const monthly = activeMonthRange.map(m => {
        const monthNum = parseInt(m, 10);
        const targetMonth = (deptRow.monthlyData || []).find(d => d.dtMonth === monthNum);
        return targetMonth ? (targetMonth.paySbuCmn || 0) : 0;
      });

      const total = monthly.reduce((sum, val) => sum + val, 0);
      const rateNum = budget > 0 ? Math.round((total / budget) * 100) : 0;

      return {
        id: deptRow.id,
        dept: deptRow.dept,
        budget: budget,
        monthly: monthly,
        total: total,
        rate: `${rateNum}%`,
        balance: budget - total,
        isMyDept: deptRow.isMyDept,
      };
    });
  }, [deptList, activeMonthRange]);

  const totals = useMemo(() => {
    const budgetSum = rows.reduce((acc, row) => acc + (row.budget || 0), 0);
    const totalSum = rows.reduce((acc, row) => acc + (row.total || 0), 0);
    const monthlySum = activeMonthRange.map((_, idx) => 
      rows.reduce((acc, row) => acc + (row.monthly?.[idx] || 0), 0)
    );
    
    const rateAvg = budgetSum > 0 ? Math.round((totalSum / budgetSum) * 100) + '%' : '0%';

    return { 
      budgetSum, 
      totalSum, 
      monthlySum, 
      rateAvg, 
      balanceSum: budgetSum - totalSum 
    };
  }, [rows, activeMonthRange]);

  useEffect(() => {
    return () => {
      scrollRefs.current = [];
    };
  }, [rows]);

  useEffect(() => {
    const triggerRightScroll = () => {
      if (scrollRefs.current.length > 0) {
        scrollRefs.current.forEach((ref) => {
          if (ref) {
            ref.scrollLeft = ref.scrollWidth - ref.clientWidth;
          }
        });
        updateArrowVisibility();
      }
    };

    const timer = setTimeout(triggerRightScroll, 100);
    return () => clearTimeout(timer);
  }, [rows, startMonth, endMonth]);

  return (
    <div className={classes.container}>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <colgroup>
            <col className={classes.colDept} /><col className={classes.colBudget} />
            <col className={classes.colScrollArea} /><col className={classes.colSummary} />
            <col className={classes.colRate} /><col className={classes.colBalance} />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className={`${classes.cell} ${classes.headerCell} ${classes.bgFirstCol}`}>부서</th>
              <th rowSpan={2} className={`${classes.cell} ${classes.headerCell} ${classes.bgBlue}`}>공통비 년간 예산</th>
              <th colSpan={3} className={`${classes.cell} ${classes.headerCell} ${classes.bgOrange}`}>
                <div className={classes.headerControlWrapper}>
                  {showLeft ? <div className={classes.arrowButton} onMouseDown={() => startScrolling('prev')} onMouseUp={stopScrolling} onMouseLeave={stopScrolling}><ChevronLeftIcon fontSize="inherit" /></div> : <div className={classes.arrowPlaceholder} />}
                  <span>전략사업본부 공통비 집행 실적 ({searchYear}년 {startMonth}월~{endMonth}월)</span>
                  {showRight ? <div className={classes.arrowButton} onMouseDown={() => startScrolling('next')} onMouseUp={stopScrolling} onMouseLeave={stopScrolling}><ChevronRightIcon fontSize="inherit" /></div> : <div className={classes.arrowPlaceholder} />}
                </div>
              </th>
              <th rowSpan={2} className={`${classes.cell} ${classes.headerCell} ${classes.bgGreen}`}>예산 잔액</th>
            </tr>
            <tr>
              <th className={classes.scrollTd}>
                <div className={classes.scrollBoxHideBar} ref={addToRefs} onScroll={handleScroll}>
                  {months.map(m => (
                    <div key={m} className={`${classes.scrollItem} ${classes.bgOrange} ${classes.monthHeaderFont}`} style={{ justifyContent: 'center'}}>
                      {m}월 집행액
                    </div>
                  ))}
                </div>
              </th>
              <th className={`${classes.cell} ${classes.highlightOrange} ${classes.headerCell} ${classes.bgOrange}`}>공통비 누계</th>
              <th className={`${classes.cell} ${classes.highlightOrange} ${classes.headerCell} ${classes.bgOrange}`}>집행률</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className={`${classes.cell} ${classes.bgFirstCol}`}>{row.dept}</td>
                <td className={`${classes.cell} ${classes.txtRight}`}>{Math.round(row.budget || 0).toLocaleString()}</td>
                <td className={classes.scrollTd}>
                  <div className={classes.scrollBoxHideBar} ref={addToRefs} onScroll={handleScroll}>
                    {row.monthly.map((val, idx) => (
                      <div key={idx} className={classes.scrollItem} style={{ justifyContent: 'flex-end' }}>{val.toLocaleString()}</div>
                    ))}
                  </div>
                </td>
                <td className={`${classes.cell} ${classes.txtRight} ${classes.bold}`}>{row.total.toLocaleString()}</td>
                <td className={`${classes.cell} ${classes.txtCenter}`}>{row.rate}</td>
                <td className={`${classes.cell} ${classes.txtRight} ${classes.bold}`}>{Math.round(row.balance || 0).toLocaleString()}</td>
              </tr>
            ))}
            {rows[0]?.isMyDept && (
            <tr>
              <td className={`${classes.cell} ${classes.bgSumRow} ${classes.txtCenter}`}>합계</td>
              <td className={`${classes.cell} ${classes.bgSumRow} ${classes.txtRight}`}>{totals.budgetSum.toLocaleString()}</td>
              <td className={classes.scrollTd}>
                <div className={classes.scrollBoxHideBar} ref={addToRefs} onScroll={handleScroll}>
                  {totals.monthlySum.map((val, idx) => (
                    <div key={idx} className={`${classes.scrollItem} ${classes.bgSumRow}`} style={{ justifyContent: 'flex-end' }}>{val.toLocaleString()}</div>
                  ))}
                </div>
              </td>
              <td className={`${classes.cell} ${classes.bgSumRow} ${classes.txtRight}`}>{totals.totalSum.toLocaleString()}</td>
              <td className={`${classes.cell} ${classes.bgSumRow} ${classes.txtCenter}`}>{totals.rateAvg}</td>
              <td className={`${classes.cell} ${classes.bgSumRow} ${classes.txtRight}`}>{totals.balanceSum.toLocaleString()}</td>
            </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BudgetToActualDirectTable;
